import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/table';
import { apiFetch } from '@/utils/api';
import { ArrowLeft, Edit, Save, X, Plus, Trash2, Star } from 'lucide-react';
import { CurrentUserProvider } from '@/hooks/useCurrentUser';
import { TagSelector } from '@/components/TagSelector';
import type { Tag } from '@/components/TagBadge';
import CategoryDropdown from '@/components/CategoryDropdown';
import { useI18n } from '@/hooks/useI18n';

interface CostCategory {
    id: string;
    name: string;
}

interface RevenueCategory {
    id: string;
    name: string;
}

interface VendorBankAccount {
    id: string;
    vendor_id: string;
    account_number?: string;
    iban?: string;
    domestic_account_number?: string;
    bank_name?: string;
    is_primary: boolean;
    is_active: boolean;
}

interface Vendor {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    tax_id?: string;
    cost_category?: CostCategory;
    cost_category_id?: string;
    revenue_category?: RevenueCategory;
    revenue_category_id?: string;
    bank_accounts?: VendorBankAccount[];
    auto_create_invoices: boolean;
    is_active: boolean;
}

interface BankAccountForm {
    account_number?: string;
    iban?: string;
    domestic_account_number?: string;
    bank_name?: string;
    is_primary: boolean;
}

export default function VendorDetailPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const { id } = params;
    const { t } = useI18n();
    const [vendor, setVendor] = useState<Vendor | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [vendorTags, setVendorTags] = useState<Tag[]>([]);

    // Bank account management
    const [showBankAccountForm, setShowBankAccountForm] = useState<boolean>(false);
    const [editingBankAccountId, setEditingBankAccountId] = useState<string | null>(null);
    const [bankAccountForm, setBankAccountForm] = useState<BankAccountForm>({
        account_number: '',
        iban: '',
        domestic_account_number: '',
        bank_name: '',
        is_primary: false,
    });

    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        tax_id: '',
        cost_category_id: '',
        revenue_category_id: '',
        auto_create_invoices: false,
    });

    useEffect(() => {
        if (id) {
            fetchVendor();
            loadVendorTags();
        }
    }, [id]);

    const loadVendorTags = async () => {
        try {
            const response = await apiFetch(`/api/tags/entities/vendors/${id}`);
            if (response.ok) {
                const data = await response.json();
                setVendorTags(data.map((item: any) => item.tag));
            }
        } catch (error) {
            console.error('Failed to load vendor tags:', error);
        }
    };

    const fetchVendor = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiFetch(`/api/vendors/${id}`);
            if (response.ok) {
                const data = await response.json();
                setVendor(data);
                setEditForm({
                    name: data.name || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    address: data.address || '',
                    tax_id: data.tax_id || '',
                    cost_category_id: data.cost_category_id || '',
                    revenue_category_id: data.revenue_category_id || '',
                    auto_create_invoices: data.auto_create_invoices || false,
                });
            } else {
                setError(t('vendors.vendorDetail.loadError', 'finance'));
            }
        } catch (error) {
            console.error('Error fetching vendor:', error);
            setError(t('vendors.vendorDetail.loadError', 'finance'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            // Convert empty strings to null for optional UUID fields
            const payload = {
                ...editForm,
                cost_category_id: editForm.cost_category_id || null,
                revenue_category_id: editForm.revenue_category_id || null,
            };

            const response = await apiFetch(`/api/vendors/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                await fetchVendor();
                setIsEditing(false);
            } else {
                const errorData = await response.json();
                alert(errorData.detail || t('vendors.vendorDetail.updateError', 'finance'));
            }
        } catch (error) {
            console.error('Error updating vendor:', error);
            alert(t('vendors.vendorDetail.updateError', 'finance'));
        }
    };

    const handleCancel = () => {
        if (vendor) {
            setEditForm({
                name: vendor.name || '',
                email: vendor.email || '',
                phone: vendor.phone || '',
                address: vendor.address || '',
                tax_id: vendor.tax_id || '',
                cost_category_id: vendor.cost_category_id || '',
                revenue_category_id: vendor.revenue_category_id || '',
                auto_create_invoices: vendor.auto_create_invoices || false,
            });
        }
        setIsEditing(false);
    };

    const handleAddBankAccount = () => {
        setBankAccountForm({
            account_number: '',
            iban: '',
            domestic_account_number: '',
            bank_name: '',
            is_primary: false,
        });
        setEditingBankAccountId(null);
        setShowBankAccountForm(true);
    };

    const handleEditBankAccount = (account: VendorBankAccount) => {
        setBankAccountForm({
            account_number: account.account_number || '',
            iban: account.iban || '',
            domestic_account_number: account.domestic_account_number || '',
            bank_name: account.bank_name || '',
            is_primary: account.is_primary,
        });
        setEditingBankAccountId(account.id);
        setShowBankAccountForm(true);
    };

    const handleSaveBankAccount = async () => {
        try {
            const url = editingBankAccountId
                ? `/api/vendors/${id}/bank-accounts/${editingBankAccountId}`
                : `/api/vendors/${id}/bank-accounts`;

            const method = editingBankAccountId ? 'PATCH' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bankAccountForm),
            });

            if (response.ok) {
                await fetchVendor();
                setShowBankAccountForm(false);
                setBankAccountForm({
                    account_number: '',
                    iban: '',
                    domestic_account_number: '',
                    bank_name: '',
                    is_primary: false,
                });
                setEditingBankAccountId(null);
            } else {
                const errorData = await response.json();
                alert(errorData.detail || t('vendors.vendorDetail.bankAccount.saveError', 'finance'));
            }
        } catch (error) {
            console.error('Error saving bank account:', error);
            alert(t('vendors.vendorDetail.bankAccount.saveError', 'finance'));
        }
    };

    const handleDeleteBankAccount = async (accountId: string) => {
        if (!confirm(t('vendors.vendorDetail.bankAccount.deleteConfirm', 'finance'))) {
            return;
        }

        try {
            const response = await apiFetch(`/api/vendors/${id}/bank-accounts/${accountId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await fetchVendor();
            } else {
                const errorData = await response.json();
                alert(errorData.detail || t('vendors.vendorDetail.bankAccount.deleteError', 'finance'));
            }
        } catch (error) {
            console.error('Error deleting bank account:', error);
            alert(t('vendors.vendorDetail.bankAccount.deleteError', 'finance'));
        }
    };

    if (loading) {
        return (
            <CurrentUserProvider>
                <Layout>
                    <div className="space-y-6">
                        <Skeleton className="h-8 w-64" />
                        <Skeleton className="h-96 w-full" />
                    </div>
                </Layout>
            </CurrentUserProvider>
        );
    }

    if (error || !vendor) {
        return (
            <CurrentUserProvider>
                <Layout>
                    <div className="space-y-6">
                        <Button variant="ghost" onClick={() => navigate({ to: '/partners' })}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            {t('vendors.vendorDetail.backToPartners', 'finance')}
                        </Button>
                        <div className="text-center py-12">
                            <p className="text-destructive">{error || t('vendors.vendorDetail.notFound', 'finance')}</p>
                        </div>
                    </div>
                </Layout>
            </CurrentUserProvider>
        );
    }

    return (
        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button variant="ghost" onClick={() => navigate({ to: '/partners' })}>
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                {t('actions.back', 'common')}
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight">{vendor.name}</h1>
                                <p className="text-muted-foreground">{t('vendors.vendorDetail.title', 'finance')}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {isEditing ? (
                                <>
                                    <Button variant="outline" onClick={handleCancel}>
                                        <X className="mr-2 h-4 w-4" />
                                        {t('actions.cancel', 'common')}
                                    </Button>
                                    <Button onClick={handleSave}>
                                        <Save className="mr-2 h-4 w-4" />
                                        {t('actions.save', 'common')}
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={() => setIsEditing(true)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    {t('actions.edit', 'common')}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Vendor Information Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('vendors.vendorDetail.information', 'finance')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>{t('vendors.name', 'finance')}</Label>
                                    {isEditing ? (
                                        <Input
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            placeholder={t('vendors.vendorDetail.vendorNamePlaceholder', 'finance')}
                                        />
                                    ) : (
                                        <p className="text-sm">{vendor.name}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>{t('vendors.taxId', 'finance')}</Label>
                                    {isEditing ? (
                                        <Input
                                            value={editForm.tax_id}
                                            onChange={(e) => setEditForm({ ...editForm, tax_id: e.target.value })}
                                            placeholder={t('vendors.taxId', 'finance')}
                                        />
                                    ) : (
                                        <p className="text-sm">{vendor.tax_id || t('common.na', 'common')}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>{t('vendors.email', 'finance')}</Label>
                                    {isEditing ? (
                                        <Input
                                            value={editForm.email}
                                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                            placeholder={t('vendors.email', 'finance')}
                                            type="email"
                                        />
                                    ) : (
                                        <p className="text-sm">{vendor.email || t('common.na', 'common')}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>{t('vendors.phone', 'finance')}</Label>
                                    {isEditing ? (
                                        <Input
                                            value={editForm.phone}
                                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                            placeholder={t('vendors.phone', 'finance')}
                                        />
                                    ) : (
                                        <p className="text-sm">{vendor.phone || t('common.na', 'common')}</p>
                                    )}
                                </div>

                                <div className="space-y-2 col-span-2">
                                    <Label>{t('vendors.address', 'finance')}</Label>
                                    {isEditing ? (
                                        <Input
                                            value={editForm.address}
                                            onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                            placeholder={t('vendors.address', 'finance')}
                                        />
                                    ) : (
                                        <p className="text-sm">{vendor.address || t('common.na', 'common')}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>{t('vendors.costCategory', 'finance')}</Label>
                                    {isEditing ? (
                                        <CategoryDropdown
                                            selectedCategoryId={editForm.cost_category_id || null}
                                            onCategoryChange={(categoryId) => setEditForm({ ...editForm, cost_category_id: categoryId || '' })}
                                            placeholder={t('vendors.vendorDetail.selectCategory', 'finance')}
                                            includeAllOption={true}
                                        />
                                    ) : (
                                        <p className="text-sm">{vendor.cost_category?.name || t('common.na', 'common')}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>{t('vendors.revenueCategory', 'finance')}</Label>
                                    {isEditing ? (
                                        <CategoryDropdown
                                            selectedCategoryId={editForm.revenue_category_id || null}
                                            onCategoryChange={(categoryId) => setEditForm({ ...editForm, revenue_category_id: categoryId || '' })}
                                            placeholder={t('vendors.vendorDetail.selectRevenueCategory', 'finance')}
                                            includeAllOption={true}
                                            categoryType="revenue"
                                        />
                                    ) : (
                                        <p className="text-sm">{vendor.revenue_category?.name || t('common.na', 'common')}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>{t('vendors.status', 'finance')}</Label>
                                    <div>
                                        <Badge variant={vendor.is_active ? 'default' : 'secondary'}>
                                            {vendor.is_active ? t('common.active', 'common') : t('common.inactive', 'common')}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            {/* Google Drive Auto-Create Invoices */}
                            <div className="pt-4 border-t">
                                <div className="flex items-center space-x-2">
                                    {isEditing ? (
                                        <>
                                            <Checkbox
                                                id="auto_create_invoices"
                                                checked={editForm.auto_create_invoices}
                                                onCheckedChange={(checked) =>
                                                    setEditForm({ ...editForm, auto_create_invoices: checked === true })
                                                }
                                            />
                                            <Label htmlFor="auto_create_invoices" className="text-sm font-normal cursor-pointer">
                                                {t('vendors.vendorDetail.autoCreateInvoicesLabel', 'finance')}
                                            </Label>
                                        </>
                                    ) : (
                                        <>
                                            <Checkbox
                                                id="auto_create_invoices_readonly"
                                                checked={vendor.auto_create_invoices}
                                                disabled
                                            />
                                            <Label htmlFor="auto_create_invoices_readonly" className="text-sm font-normal">
                                                {t('vendors.vendorDetail.autoCreateInvoicesLabel', 'finance')}
                                            </Label>
                                        </>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 ml-6">
                                    {t('vendors.vendorDetail.autoCreateInvoicesHint', 'finance')}
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Tags */}
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('vendors.tags', 'finance')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <TagSelector
                                entityType="vendors"
                                entityId={vendor.id}
                                selectedTags={vendorTags}
                                onChange={setVendorTags}
                            />
                        </CardContent>
                    </Card>

                    {/* Bank Accounts */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>{t('vendors.vendorDetail.bankAccounts.title', 'finance')}</CardTitle>
                                <Button onClick={handleAddBankAccount} size="sm">
                                    <Plus className="mr-2 h-4 w-4" />
                                    {t('vendors.vendorDetail.bankAccounts.add', 'finance')}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {showBankAccountForm && (
                                <div className="mb-6 p-4 border rounded-lg space-y-4 bg-muted/50">
                                    <h3 className="font-semibold">
                                        {editingBankAccountId
                                            ? t('vendors.vendorDetail.bankAccounts.edit', 'finance')
                                            : t('vendors.vendorDetail.bankAccounts.new', 'finance')}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>{t('vendors.vendorDetail.bankAccounts.iban', 'finance')}</Label>
                                            <Input
                                                value={bankAccountForm.iban}
                                                onChange={(e) => setBankAccountForm({ ...bankAccountForm, iban: e.target.value })}
                                                placeholder={t('vendors.vendorDetail.bankAccounts.ibanPlaceholder', 'finance')}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>{t('vendors.vendorDetail.bankAccounts.domesticAccountNumber', 'finance')}</Label>
                                            <Input
                                                value={bankAccountForm.domestic_account_number}
                                                onChange={(e) => setBankAccountForm({ ...bankAccountForm, domestic_account_number: e.target.value })}
                                                placeholder={t('vendors.vendorDetail.bankAccounts.domesticAccountNumberPlaceholder', 'finance')}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>{t('vendors.vendorDetail.bankAccounts.accountNumberOther', 'finance')}</Label>
                                            <Input
                                                value={bankAccountForm.account_number}
                                                onChange={(e) => setBankAccountForm({ ...bankAccountForm, account_number: e.target.value })}
                                                placeholder={t('vendors.vendorDetail.bankAccounts.accountNumberPlaceholder', 'finance')}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>{t('vendors.vendorDetail.bankAccounts.bankName', 'finance')}</Label>
                                            <Input
                                                value={bankAccountForm.bank_name}
                                                onChange={(e) => setBankAccountForm({ ...bankAccountForm, bank_name: e.target.value })}
                                                placeholder={t('vendors.vendorDetail.bankAccounts.bankNamePlaceholder', 'finance')}
                                            />
                                        </div>
                                        <div className="space-y-2 col-span-2">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="is_primary"
                                                    checked={bankAccountForm.is_primary}
                                                    onChange={(e) => setBankAccountForm({ ...bankAccountForm, is_primary: e.target.checked })}
                                                    className="h-4 w-4"
                                                />
                                                <Label htmlFor="is_primary" className="cursor-pointer">
                                                    {t('vendors.vendorDetail.bankAccounts.setPrimary', 'finance')}
                                                </Label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button onClick={handleSaveBankAccount}>
                                            <Save className="mr-2 h-4 w-4" />
                                            {t('actions.save', 'common')}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setShowBankAccountForm(false);
                                                setEditingBankAccountId(null);
                                            }}
                                        >
                                            {t('actions.cancel', 'common')}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {vendor.bank_accounts && vendor.bank_accounts.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('vendors.vendorDetail.bankAccounts.primary', 'finance')}</TableHead>
                                            <TableHead>{t('vendors.vendorDetail.bankAccounts.iban', 'finance')}</TableHead>
                                            <TableHead>{t('vendors.vendorDetail.bankAccounts.domesticAccount', 'finance')}</TableHead>
                                            <TableHead>{t('vendors.vendorDetail.bankAccounts.accountNumber', 'finance')}</TableHead>
                                            <TableHead>{t('vendors.vendorDetail.bankAccounts.bankName', 'finance')}</TableHead>
                                            <TableHead>{t('actions.actions', 'common')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {vendor.bank_accounts.filter(acc => acc.is_active).map((account) => (
                                            <TableRow key={account.id}>
                                                <TableCell>
                                                    {account.is_primary && (
                                                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                                                    )}
                                                </TableCell>
                                                <TableCell>{account.iban || t('common.na', 'common')}</TableCell>
                                                <TableCell>{account.domestic_account_number || t('common.na', 'common')}</TableCell>
                                                <TableCell>{account.account_number || t('common.na', 'common')}</TableCell>
                                                <TableCell>{account.bank_name || t('common.na', 'common')}</TableCell>
                                                <TableCell>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEditBankAccount(account)}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteBankAccount(account.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <p className="text-sm text-muted-foreground">{t('vendors.vendorDetail.bankAccounts.none', 'finance')}</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}

export const Route = createFileRoute('/partners/$id')({
    component: VendorDetailPage,
})
